import { AdFrame } from '@generated/components/AdEdits';
import { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { Download, Loader2 } from 'lucide-react';

type Paper = { id: string; label: string; paper: string; area: string; price: string; bg: string; fg: string; accent: string };
type Variant = { key: string; p: Paper; free: boolean };
const PAPERS: Paper[] = [
  { id: 'redhook', label: 'Red Hook Star-Revue', paper: 'Red Hook Star-Revue', area: 'Red Hook · Carroll Gardens · Cobble Hill · Gowanus', price: '$5', bg: 'bg-primary', fg: 'text-primary-foreground', accent: 'bg-[hsl(var(--brand-yellow))]' },
  { id: 'village', label: 'The Village Star-Revue', paper: 'The Village Star-Revue', area: 'Greenwich Village\nEast Village\nSoHo', price: '$5', bg: 'bg-[hsl(var(--brand-blue))]', fg: 'text-primary-foreground', accent: 'bg-[hsl(var(--brand-yellow))]' },
  { id: 'both', label: 'Both papers', paper: 'Both Star-Revues', area: 'Brooklyn + Manhattan, one ad', price: '$10', bg: 'bg-[hsl(var(--brand-yellow))]', fg: 'text-foreground', accent: 'bg-card' },
];
const CATS = ['For Sale', 'Help Wanted', 'Housing', 'Services', 'Lost & Found', 'Missed Connections'];
const SETS = [['Free through December 31 · our thanks to readers', true], ['Regular price', false]] as const;

function Ad({ p, free }: { p: Paper; free: boolean }) {
  return (
    <div className={`flex h-[540px] w-[540px] flex-col justify-between border-[10px] border-foreground p-8 font-sans ${p.bg} ${p.fg}`}>
      <div>
        {!free && <div className="flex items-center justify-between text-[13px] font-bold uppercase tracking-[0.2em]"><span>{p.paper}</span><span>Classifieds</span></div>}
        {p.id === "both" && !free && <div className="mt-2 flex h-3"><div className="flex-1 bg-primary" /><div className="flex-1 bg-[hsl(var(--brand-blue))]" /></div>}
        {free ? (<>
          <div className={`inline-block border-4 border-foreground px-3 py-1 text-[15px] font-black uppercase tracking-[0.15em] text-foreground ${p.accent}`}>Our thanks to our readers</div>
          <div className="-mx-2 mt-3 whitespace-nowrap text-[76px] font-black uppercase leading-[0.86] tracking-[-0.05em]">Free<br />classifieds</div><div className="mt-2 text-[28px] font-black leading-tight">in the Star-Revue · thru Dec 31</div>
          <p className="mt-2 text-[17px] font-semibold leading-snug">Selling, hiring, found a cat? Place it at no charge.</p>
        </>) : (<>
          <div className="mt-6 text-[64px] font-black uppercase leading-[0.92] tracking-tight">Say it in<br />the paper.</div>
          <p className="mt-4 text-[19px] font-semibold leading-snug">Selling a couch? Hiring help? Found a cat?<br />Your neighbors still read the classifieds.</p>
        </>)}
      </div>
      <div className="flex flex-wrap gap-2">{CATS.map((c) => <span key={c} className="border-2 border-foreground bg-card px-2.5 py-1 text-[13px] font-bold text-foreground">{c}</span>)}</div>
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="whitespace-pre-line text-[13px] font-bold uppercase tracking-[0.15em] opacity-90">{p.area}</div>
          <div className="mt-1 text-[15px] font-semibold">Up to 250 characters · place it yourself on our DIY portal</div>
        </div>
        <div className={`shrink-0 border-4 border-foreground px-4 py-2 text-center text-foreground shadow-[5px_5px_0_hsl(var(--foreground))] ${p.accent}`}>
          {free ? (<>
            <div className="text-[13px] font-bold uppercase tracking-wider line-through opacity-70">{p.price}</div>
            <div className="text-[44px] font-black leading-none">FREE</div>
            <div className="text-[11px] font-bold uppercase tracking-wider">thru Dec 31</div>
          </>) : (<>
            <div className="text-[44px] font-black leading-none">{p.price}</div>
            <div className="text-[11px] font-bold uppercase tracking-wider">per ad</div>
          </>)}
        </div>
      </div>
    </div>
  );
}

/** Square Instagram ads (1080 × 1080 when saved) for classifieds in each paper, regular and free-November versions. */
export function InstaClassifieds() {
  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = async (v: Variant) => {
    const node = refs.current[v.key]; if (!node) return;
    setBusy(v.key); setError(null);
    try {
      const url = await toPng(node, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement('a'); a.href = url; a.download = `instagram-classifieds-${v.key}.png`; a.click();
    } catch (e) { console.error(e); setError('That image could not be saved. Please try again.'); }
    finally { setBusy(null); }
  };
  return (
    <section className="mt-14 border-t-4 border-double border-foreground pt-8">
      <h2 className="font-[family-name:var(--font-heading)] text-3xl font-semibold">Instagram ads · Classifieds</h2>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Square posts that save at 1080 × 1080, Instagram's size. Put the portal link in your bio or story link sticker, since Instagram doesn't allow links in post captions.</p>
      {error && <p role="alert" className="mt-3 text-sm text-destructive">{error}</p>}
      {SETS.map(([title, free]) => (
        <div key={title} className="mt-8">
          <h3 className="border-b-2 border-foreground pb-1 text-xl font-semibold">{title}</h3>
          <div className="mt-4 grid gap-8 xl:grid-cols-2">{PAPERS.map((p) => {
            const v: Variant = { key: `${p.id}${free ? '-free-thru-dec31' : ''}`, p, free };
            return (
              <div key={v.key} className="min-w-0">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2"><h4 className="text-lg font-medium">{p.label}</h4>
                  <button type="button" disabled={!!busy} onClick={() => void save(v)} className="inline-flex min-h-11 items-center gap-2 border border-foreground px-4 text-sm font-semibold disabled:opacity-50">
                    {busy === v.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Save for Instagram</button></div>
                <AdFrame adId={`insta-${v.key}`} label={`${p.label}${free ? " (free)" : ""}`} capture={(el) => { refs.current[v.key] = el; }} className="w-[540px] shrink-0"><Ad p={p} free={free} /></AdFrame>
              </div>
            );
          })}</div>
        </div>
      ))}
    </section>
  );
}
